import { generateUUID } from '@wix/form-schema';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ComboBox as ComboBoxRAC,
  Input as InputRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  TextField as TextFieldRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaStreetNameProps } from '../../../fields/mla-street-name/mla-street-name/mla-street-name-props';
import { useAddressAutofill } from '../../../fields/multiline-address/multiline-address/address-autofill-context';
import { useCountryCode } from '../../../fields/multiline-address/multiline-address/country-code-context';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

import useAtlasHeadless from './use-atlas-headless';

export type StreetNameProps = {
  children: ReactNode;
};

export const StreetName = ({ children }: StreetNameProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
    autocomplete,
  } = useFieldPropsV2<MlaStreetNameProps>();

  const { autocompleteEnabled } = useAddressAutofill();
  const isAutocomplete = autocomplete || autocompleteEnabled;

  const streetNameLabel = useAddressLabel('streetName');
  const labelElement = findChildOfType(children, StreetName.Label);
  const inputElement = findChildOfType(children, StreetName.Input);
  const optionsElement = findChildOfType(children, StreetName.Options);
  const descriptionElement = findChildOfType(children, StreetName.Description);
  const errorElement = findChildOfType(children, StreetName.Error);

  if (isAutocomplete) {
    return (
      <FieldLayout
        fieldId={id}
        renderLabel={() =>
          applyInternalProps<LabelInternalProps>(labelElement, {
            label: streetNameLabel,
          })
        }
        renderInput={() => (
          <StreetNameAutocomplete
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            hasError={hasError}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            {inputElement}
            {optionsElement}
          </StreetNameAutocomplete>
        )}
        renderDescription={() => (
          <>
            {descriptionElement}
            {errorElement}
          </>
        )}
      />
    );
  }

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: streetNameLabel,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type StreetNameAutocompleteProps = {
  children: ReactNode;
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

const StreetNameAutocomplete = ({
  children,
  value,
  onChange,
  disabled,
  required,
  hasError,
  onBlur,
  onFocus,
}: StreetNameAutocompleteProps) => {
  const { onAddressSelect } = useAddressAutofill();
  const countryCode = useCountryCode();
  const [sessionToken, setSessionToken] = useState<string>('');
  const { predictions, updatePredictions, clearPredictions, getPlace } =
    useAtlasHeadless({
      sessionToken,
    });

  const inputElement = findChildOfType(children, StreetName.Input);
  const optionsElement = findChildOfType(children, StreetName.Options);

  const options = useMemo(
    () =>
      predictions.reduce<{ id: string; label: string }[]>(
        (result, prediction) => {
          if (prediction.searchId && prediction.description) {
            result.push({
              id: prediction.searchId,
              label: prediction.description,
            });
          }
          return result;
        },
        [],
      ),
    [predictions],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      const countryCodes = countryCode ? [countryCode] : undefined;
      updatePredictions({ input: newValue, countryCodes });
      onChange(newValue);
    },
    [onChange, updatePredictions, countryCode],
  );

  const handleSelectionChange = useCallback(
    async (key: React.Key | null) => {
      clearPredictions();
      if (!key) {
        return;
      }

      const address = await getPlace(String(key));

      if (address && onAddressSelect) {
        onAddressSelect(address);
      }
    },
    [onAddressSelect, clearPredictions, getPlace],
  );

  const handleBlur = useCallback(() => {
    setSessionToken('');
    clearPredictions();
    onBlur?.();
  }, [onBlur, clearPredictions]);

  const handleFocus = useCallback(() => {
    setSessionToken(generateUUID());
    onFocus?.();
  }, [onFocus]);

  return (
    <ComboBoxRAC
      inputValue={value ?? ''}
      onInputChange={handleInputChange}
      selectedKey={null}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      isRequired={required}
      isInvalid={hasError}
      onBlur={handleBlur}
      onFocus={handleFocus}
      allowsCustomValue
      menuTrigger="input"
      items={options}
    >
      {applyInternalProps<InputInternalProps>(inputElement, {
        onFocus: handleFocus,
      })}
      {applyInternalProps<OptionsInternalProps>(optionsElement, {
        options,
      })}
    </ComboBoxRAC>
  );
};

type InputProps = {
  className?: string;
};

type InputInternalProps = {
  onFocus?: () => void;
};

const Input = ({ className, ...internal }: InputProps) => {
  const { id } = useFieldPropsV2<MlaStreetNameProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { onFocus } = internal as InputInternalProps;

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      onFocus={onFocus}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  options: { id: string; label: string }[];
};

const Options = ({ children, className }: OptionsProps) => {
  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC renderEmptyState={() => null}>
        {(item: { id: string; label: string }) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            value: item.id,
            label: item.label,
          })
        }
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { value, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

StreetName.Label = Label;
StreetName.Input = Input;
StreetName.Options = Options;
StreetName.Description = Description;
StreetName.Error = Error;
